import { ITaskChannel } from "./IExternalAgent";
import styles from "./styles.module.css";

interface IProps {
  taskChannels: Array<ITaskChannel>;
  onChange: (updatedChannels: Array<ITaskChannel>) => void;
}

/**
 * Per-channel capacity for a worker: how many voice tasks, chat tasks and so on TaskRouter
 * may hold against this agent at once, and whether the channel is offered at all.
 *
 * Fed by /external-agents/{id}/channels, the counterpart to the internal-agent
 * /agents/{agentId}/channels. Rendered only for an agent that already has a worker — the
 * channels are TaskRouter's, keyed by the worker SID, so there is nothing to show before one
 * exists. Edits are held until Apply rather than written per keystroke.
 */
const TaskChannels = ({ taskChannels, onChange }: IProps) => {
  const replace = (index: number, patch: Partial<ITaskChannel>): void => {
    // Copy the row being edited rather than assigning through the shallow array copy —
    // `[...channels]` shares its objects with the array the previous render is holding, so
    // the old code mutated live state and could render the stale value back.
    onChange(
      taskChannels.map((channel, i) =>
        i === index ? { ...channel, ...patch } : channel
      )
    );
  };

  return (
    <div className={styles.Panel}>
      <div className={styles.PanelTitle}>Task Channels</div>
      <hr className={styles.PanelRule} />
      {taskChannels.map((channel: ITaskChannel, index: number) => (
        // The label is a fixed column so every capacity box lines up; previously the name and
        // the input simply sat next to each other, so a row with a long name like "voicemail"
        // pushed its control further right than the one above it.
        <div key={channel.sid} className={styles.Row}>
          <label className={styles.RowLabel} htmlFor={"channel-" + channel.sid}>
            {channel.name}
          </label>
          <input
            id={"channel-" + channel.sid}
            className={`${styles.Control} ${styles.Capacity}`}
            type="number"
            min={0}
            value={channel.capacity}
            onChange={(e) => {
              const capacity = parseInt(e.target.value, 10);
              // An emptied field parses to NaN, which React renders as a blank controlled
              // input and the API rejects. Zero is the meaningful floor.
              replace(index, { capacity: isNaN(capacity) ? 0 : capacity });
            }}
          />
          <input
            className={styles.RowToggle}
            type="checkbox"
            checked={channel.available}
            aria-label={channel.name + " available"}
            onChange={(e) => replace(index, { available: e.target.checked })}
          />
        </div>
      ))}
    </div>
  );
};

export default TaskChannels;
